import { createApi } from "@reduxjs/toolkit/query/react";

import { TokenService } from "@/providers/tokens/tokens.service";
import { triggerFileDownload } from "@/shared/helpers/trigger-file-download.helper";
import { type IApiResponse } from "@/shared/interfaces/api-response.interface";
import type {
  IBoundaryGeometryResponse,
  IBoundaryGeometriesPageResponse,
} from "@/shared/interfaces/boundary-geometry.interface";

import { setDistricts, setSelectedDistrict } from "../../slices/district/slice";
import { ApiTags } from "../api-tags.enum";
import customFetchBase from "../custom-fetch-base";
import { type SchoolTypeEnum } from "../reports/reports.interface";
import schoolsApi from "../schools/schools";
import { type StudentImportType } from "../students/students.interface";

import {
  type IDashboardChart,
  type IDashboardResponse,
  type IDashboardStatsResponse,
  type IDistrict,
  type IDistrictDataCheckResponse,
  type IDistrictHasNewHousingResponse,
  type IDistrictMetadata,
  type INewHousingCountsResponse,
  type IDistrictSchoolsListResponse,
  type IDownloadDistrictBoundaryZipSuccessResult,
  type IExportDistrictBoundaryZipArg,
  type IEnqueueDistrictImportResponse,
  type IFinalizeDistrictBoundaryRequest,
  type IFinalizeDistrictBoundaryResponse,
  type IGetDistrictDataCheckParams,
  type IImportDistrictBoundaryRequest,
  type IDistrictBoundaryPreviewListResponse,
  type IDistrictBoundaryPreviewResponse,
  type IDistrictAttendanceSchoolOptionsResponse,
  type IGetDistrictBoundaryPreviewDetailsParams,
  type IGetDistrictBoundaryPreviewGeometriesParams,
  type ISchoolColorPaletteResponse,
  type ISchoolLevel,
  type IUpsertDistrictBoundaryPreviewsRequest,
} from "./districts.interface";
import { fetchDistrictBoundaryZip } from "./functions";

/** Levels list only (Map layer options etc.); school mutations invalidate it without refetching every district query. */
export const DISTRICT_LEVELS_TAG = { type: ApiTags.Districts, id: "LEVELS" } as const;

const districtsApi = createApi({
  reducerPath: "districtsApi",
  baseQuery: customFetchBase,
  tagTypes: [ApiTags.Districts, ApiTags.BoundaryGeometry],
  endpoints: (builder) => ({
    getDistricts: builder.query<IDistrict[], void>({
      query: () => ({
        url: "/districts",
        method: "GET",
      }),
      providesTags: [ApiTags.Districts],
      transformResponse: (result: IApiResponse<IDistrict[]>) => result.data ?? [],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          if (!data || data.length === 0) {
            if (typeof window !== "undefined") {
              localStorage.removeItem("districtId");
            }
            dispatch(setSelectedDistrict(null));
            return;
          }

          dispatch(setDistricts(data));

          const districtId = TokenService.getDistrictId();
          
          let district = districtId ? data.find((d) => d.id === districtId) : null;
          
          if (!district && data.length > 0) {
            district = data[0];
          }

          if (district) {
            TokenService.setDistrictId(district.id);
            dispatch(setSelectedDistrict(district));
          } else {
            if (typeof window !== "undefined") {
              localStorage.removeItem("districtId");
            }
            dispatch(setSelectedDistrict(null));
          }
        } catch (error) {
          console.warn("Failed to get districts:", error);
        }
      },
    }),
    importDistrictBoundary: builder.mutation<
      IEnqueueDistrictImportResponse,
      { districtId: string; body: IImportDistrictBoundaryRequest }
    >({
      query: ({ districtId, body }) => ({
        url: `/districts/${districtId}/import/boundary`,
        method: "POST",
        body,
      }),
      transformResponse: (result: IApiResponse<IEnqueueDistrictImportResponse>) => result.data,
    }),
    finalizeDistrictBoundary: builder.mutation<
      IFinalizeDistrictBoundaryResponse,
      IFinalizeDistrictBoundaryRequest
    >({
      query: (body) => ({
        url: `/districts/${body.districtId}/import/boundary/finalize`,
        method: "POST",
        body,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(districtsApi.util.invalidateTags([ApiTags.Districts]));
          dispatch(
            schoolsApi.util.invalidateTags([{ type: ApiTags.Documents }]),
          );
        } catch {
          // no-op
        }
      },
      transformResponse: (result: IApiResponse<IFinalizeDistrictBoundaryResponse>) => result.data,
    }),
    getDistrictBoundaryPreviewDetails: builder.query<
      IDistrictBoundaryPreviewResponse,
      IGetDistrictBoundaryPreviewDetailsParams
    >({
      query: ({ districtId, previewId }) => ({
        url: `/districts/${districtId}/import/boundary/previews/${previewId}`,
        method: "GET",
      }),
      transformResponse: (result: IApiResponse<IDistrictBoundaryPreviewResponse>) => result.data,
    }),
    getDistrictBoundaryPreviewGeometry: builder.query<
      IBoundaryGeometryResponse,
      IGetDistrictBoundaryPreviewDetailsParams
    >({
      query: ({ districtId, previewId }) => ({
        url: `/districts/${districtId}/import/boundary/previews/${previewId}/geometry`,
        method: "GET",
      }),
      keepUnusedDataFor: 300,
      providesTags: (_result, _error, { districtId, previewId }) => [
        { type: ApiTags.BoundaryGeometry, id: `district-preview:${districtId}:${previewId}` },
        { type: ApiTags.BoundaryGeometry, id: `district-preview:${districtId}` },
      ],
      transformResponse: (result: IApiResponse<IBoundaryGeometryResponse>) => result.data,
    }),
    getDistrictBoundaryPreviewGeometries: builder.query<
      IBoundaryGeometriesPageResponse,
      IGetDistrictBoundaryPreviewGeometriesParams
    >({
      query: ({ districtId, documentId, cursor, limit }) => ({
        url: `/districts/${districtId}/import/boundary/previews/geometries`,
        method: "GET",
        params: {
          documentId,
          ...(cursor != null ? { cursor } : {}),
          ...(limit != null ? { limit } : {}),
        },
      }),
      keepUnusedDataFor: 300,
      providesTags: (_result, _error, { districtId, documentId }) => [
        { type: ApiTags.BoundaryGeometry, id: `district-preview:${districtId}` },
        { type: ApiTags.BoundaryGeometry, id: `district-preview-doc:${documentId}` },
      ],
      transformResponse: (result: IApiResponse<IBoundaryGeometriesPageResponse>) => result.data,
    }),
    upsertDistrictBoundaryPreviews: builder.mutation<
      IDistrictBoundaryPreviewListResponse,
      IUpsertDistrictBoundaryPreviewsRequest
    >({
      query: ({ districtId, boundaries }) => ({
        url: `/districts/${districtId}/import/boundary/previews/bulk`,
        method: "PUT",
        body: { boundaries },
      }),
      async onQueryStarted({ districtId, boundaries }, { dispatch, queryFulfilled }) {
        try {
          const { data: result } = await queryFulfilled;
          const documentIds = [...new Set(boundaries.map((boundary) => boundary.documentId))];

          dispatch(
            schoolsApi.util.invalidateTags([
              ...documentIds.map((documentId) => ({
                type: ApiTags.Documents as const,
                id: `district-boundary-previews-${documentId}`,
              })),
              ...(result?.items?.map((item) => ({
                type: ApiTags.Documents as const,
                id: `district-boundary-preview-${item.id}`,
              })) ?? []),
            ]),
          );
          dispatch(
            districtsApi.util.invalidateTags([
              ApiTags.Districts,
              { type: ApiTags.BoundaryGeometry, id: `district-preview:${districtId}` },
              ...(result?.items?.map((item) => ({
                type: ApiTags.BoundaryGeometry as const,
                id: `district-preview:${districtId}:${item.id}`,
              })) ?? []),
            ]),
          );
        } catch {
          // no-op
        }
      },
      transformResponse: (result: IApiResponse<IDistrictBoundaryPreviewListResponse>) => result.data,
    }),
    downloadDistrictBoundaryZip: builder.mutation<
      IDownloadDistrictBoundaryZipSuccessResult,
      IExportDistrictBoundaryZipArg
    >({
      queryFn: async (arg) => {
        const result = await fetchDistrictBoundaryZip(arg);

        if (result.error) {
          return { error: { status: result.error.statusCode, data: result.error } };
        }

        triggerFileDownload(result.data!.blob, result.data!.filename);
        return {
          data: {
            success: true as const,
            filename: result.data!.filename,
          },
        };
      },
    }),
    getDistrictMetadata: builder.query<IDistrictMetadata, { districtId: string }>({
      query: ({ districtId }) => ({
        url: `/districts/${districtId}/metadata`,
        method: "GET",
      }),
      providesTags: [ApiTags.Districts],
      transformResponse: (result: IApiResponse<IDistrictMetadata>) => result.data,
    }),
    getDistrictHasNewHousing: builder.query<
      IDistrictHasNewHousingResponse,
      { districtId: string }
    >({
      query: ({ districtId }) => ({
        url: `/districts/${districtId}/has-new-housing`,
        method: "GET",
      }),
      providesTags: [ApiTags.Districts],
      transformResponse: (result: IApiResponse<IDistrictHasNewHousingResponse>) =>
        result.data,
    }),
    getNewHousingCounts: builder.query<INewHousingCountsResponse, { districtId: string }>({
      query: ({ districtId }) => ({
        url: `/districts/${districtId}/new-housing-counts`,
        method: "GET",
      }),
      providesTags: [ApiTags.Districts],
      transformResponse: (result: IApiResponse<INewHousingCountsResponse>) => result.data,
    }),
    getSchoolColorPalette: builder.query<ISchoolColorPaletteResponse, { districtId: string }>({
      query: ({ districtId }) => ({
        url: `/districts/${districtId}/schools/color-palette`,
        method: "GET",
      }),
      providesTags: [ApiTags.Districts],
      transformResponse: (result: IApiResponse<ISchoolColorPaletteResponse>) => result.data,
    }),
    getDistrictLevels: builder.query<ISchoolLevel[], { districtId: string }>({
      query: ({ districtId }) => ({
        url: `/districts/${districtId}/levels`,
        method: "GET",
      }),
      providesTags: [ApiTags.Districts, DISTRICT_LEVELS_TAG],
      transformResponse: (result: IApiResponse<ISchoolLevel[]>) => result.data ?? [],
    }),
    getDistrictDataCheck: builder.query<IDistrictDataCheckResponse, IGetDistrictDataCheckParams>({
      query: ({ districtId, type, year, month, importType }) => ({
        url: `/districts/${districtId}/data-check`,
        method: "GET",
        params: {
          type,
          ...(year != null && { year }),
          ...(month != null && { month }),
          ...(importType != null && { importType }),
        },
      }),
      providesTags: [ApiTags.Districts],
      transformResponse: (result: IApiResponse<IDistrictDataCheckResponse>) =>
        result.data,
    }),
    getDashboard: builder.query<IDashboardResponse, { districtId: string }>({
      query: ({ districtId }) => ({
        url: `/districts/${districtId}/dashboard`,
        method: "GET",
      }),
      providesTags: [ApiTags.Districts],
      transformResponse: (result: IApiResponse<IDashboardResponse>) => result.data,
    }),
    getDashboardChart: builder.query<IDashboardChart, { districtId: string }>({
      query: ({ districtId }) => ({
        url: `/districts/${districtId}/dashboard/chart`,
        method: "GET",
      }),
      providesTags: [ApiTags.Districts],
      transformResponse: (result: IApiResponse<IDashboardChart>) => result.data,
    }),
    getDashboardStats: builder.query<IDashboardStatsResponse, { districtId: string }>({
      query: ({ districtId }) => ({
        url: `/districts/${districtId}/dashboard/stats`,
        method: "GET",
      }),
      providesTags: [ApiTags.Districts],
      transformResponse: (result: IApiResponse<IDashboardStatsResponse>) => result.data,
    }),
    getDistrictSchools: builder.query<
      IDistrictSchoolsListResponse,
      { districtId: string; schoolTypes?: SchoolTypeEnum[]; includeScheduledOpening?: boolean }
    >({
      query: ({ districtId, schoolTypes, includeScheduledOpening }) => ({
        url: `/districts/${districtId}/schools`,
        method: "GET",
        params: {
          ...(schoolTypes?.length ? { schoolTypes } : {}),
          ...(includeScheduledOpening ? { includeScheduledOpening: true } : {}),
        },
      }),
      providesTags: [ApiTags.Districts],
      transformResponse: (result: IApiResponse<IDistrictSchoolsListResponse>) =>
        result.data ?? { items: [] },
    }),
    getDistrictAttendanceSchoolOptions: builder.query<
      IDistrictAttendanceSchoolOptionsResponse,
      {
        districtId: string;
        year: number;
        snapshotType: StudentImportType;
        month?: number;
      }
    >({
      query: ({ districtId, year, snapshotType, month }) => ({
        url: `/districts/${districtId}/students/attendance-school-options`,
        method: "GET",
        params: {
          year,
          snapshotType,
          ...(month !== undefined ? { month } : {}),
        },
      }),
      providesTags: [ApiTags.Districts],
      transformResponse: (result: IApiResponse<IDistrictAttendanceSchoolOptionsResponse>) =>
        result.data ?? { orphanInternalIds: [], hasNoAttendanceSchoolInfo: false },
    }),
  }),
});

export const {
  useGetDistrictsQuery,
  useGetDistrictMetadataQuery,
  useGetDistrictHasNewHousingQuery,
  useGetNewHousingCountsQuery,
  useGetSchoolColorPaletteQuery,
  useGetDistrictLevelsQuery,
  useGetDistrictDataCheckQuery,
  useGetDashboardQuery,
  useGetDashboardChartQuery,
  useGetDashboardStatsQuery,
  useGetDistrictSchoolsQuery,
  useGetDistrictAttendanceSchoolOptionsQuery,
  useLazyGetDistrictSchoolsQuery,
  useImportDistrictBoundaryMutation,
  useFinalizeDistrictBoundaryMutation,
  useGetDistrictBoundaryPreviewDetailsQuery,
  useLazyGetDistrictBoundaryPreviewDetailsQuery,
  useGetDistrictBoundaryPreviewGeometryQuery,
  useLazyGetDistrictBoundaryPreviewGeometryQuery,
  useLazyGetDistrictBoundaryPreviewGeometriesQuery,
  useGetDistrictBoundaryPreviewGeometriesQuery,
  useUpsertDistrictBoundaryPreviewsMutation,
  useDownloadDistrictBoundaryZipMutation,
} = districtsApi;
export default districtsApi;

